import React, { useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { adminApi, AudienceMember } from '../redux/adminApiSlice';
import { PostCard, AdminPost } from '../components/PostCard';
import { gridTheme, gridTokens } from '../theme/gridTheme';
import ActivityPulse, { PulseAxis } from '../components/Analytics/ActivityPulse';
import BucketMembersDialog, { BucketSelection } from '../components/Analytics/BucketMembersDialog';
import { audienceAvatarUrl, audienceLocationText } from '../components/Analytics/audienceDisplay';
import PushChip from '../components/Analytics/AudiencePushChip';
import SmsSignInPanel from '../components/Analytics/SmsSignInPanel';
import UserModal from '../components/UserModal/UserModal';
import { User } from '../types/interfaces';

const ACQUISITION_SOURCES = [
  'founder_invite',
  'friend_referral',
  'venue',
  'creator',
  'event',
  'instagram',
  'paid_ad',
  'app_store',
  'other',
  'unknown',
] as const;

// Tab order: Growth is the default, plain-English view. The rest are "pro" tabs.
const TAB = {
  GROWTH: 0,
  OVERVIEW: 1,
  FUNNEL: 2,
  CONTENT: 3,
  SOCIAL: 4,
  RETENTION: 5,
  GEOGRAPHY: 6,
  COHORTS: 7,
  ENGAGEMENT: 8,
  SMS: 9,
} as const;

function MetricCard({
  label,
  value,
  help,
  onClick,
}: {
  label: string;
  value: React.ReactNode;
  help?: string;
  onClick?: () => void;
}) {
  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        ...(onClick
          ? {
              cursor: 'pointer',
              transition: 'border-color 0.15s, box-shadow 0.15s',
              '&:hover': { borderColor: 'primary.main', boxShadow: 1 },
            }
          : {}),
      }}
      onClick={onClick}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
          {help ? (
            <Tooltip title={help}>
              <InfoOutlinedIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
            </Tooltip>
          ) : null}
        </Box>
        <Typography variant="h5" sx={{ mt: 1 }}>
          {value ?? '—'}
        </Typography>
      </CardContent>
    </Card>
  );
}

function pct(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—';
  return `${(n * 100).toFixed(1)}%`;
}

type DayPoint = { date: string; count: number };

/**
 * Lightweight vertical bar chart (no chart library dependency). Renders one bar
 * per day; height is proportional to the max value in the series.
 */
function SimpleBarChart({
  data,
  color = '#1976d2',
  height = 180,
  onBarClick,
}: {
  data: DayPoint[];
  color?: string;
  height?: number;
  onBarClick?: (point: DayPoint) => void;
}) {
  if (!data || data.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        No data in this date range.
      </Typography>
    );
  }
  const max = Math.max(1, ...data.map((d) => d.count));
  const total = data.reduce((a, b) => a + b.count, 0);
  // Only label a manageable number of dates on the x-axis.
  const labelEvery = Math.ceil(data.length / 12);
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {total} total • peak {max}/day
      </Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 0.5,
          height,
          mt: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          pb: 0,
        }}
      >
        {data.map((d) => {
          const clickable = !!onBarClick && d.count > 0;
          return (
            <Tooltip
              key={d.date}
              title={`${d.date}: ${d.count}${clickable ? ' — click to view' : ''}`}
              arrow
            >
              <Box
                onClick={clickable ? () => onBarClick!(d) : undefined}
                sx={{
                  flex: 1,
                  minWidth: 4,
                  height: `${(d.count / max) * 100}%`,
                  bgcolor: color,
                  borderRadius: '2px 2px 0 0',
                  transition: 'opacity 0.15s',
                  opacity: 0.85,
                  cursor: clickable ? 'pointer' : 'default',
                  '&:hover': { opacity: 1 },
                }}
              />
            </Tooltip>
          );
        })}
      </Box>
      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
        {data.map((d, i) => (
          <Box key={d.date} sx={{ flex: 1, minWidth: 4, textAlign: 'center' }}>
            {i % labelEvery === 0 ? (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                {d.date.slice(5)}
              </Typography>
            ) : null}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function fmtDate(d: string | Date | null | undefined): string {
  if (!d) return '—';
  const date = typeof d === 'string' ? new Date(d) : d;
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtRelative(d: string | Date | null | undefined): string {
  if (!d) return 'Never';
  const date = typeof d === 'string' ? new Date(d) : d;
  if (Number.isNaN(date.getTime())) return 'Never';
  const diffMs = Date.now() - date.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return fmtDate(date);
}

const AnalyticsPage: React.FC = () => {
  const [tab, setTab] = useState<number>(TAB.GROWTH);
  const [includeExcluded, setIncludeExcluded] = useState(false);
  const [acquisitionSource, setAcquisitionSource] = useState('');
  const [from, setFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10));

  // Growth drill-in.
  const [postsUser, setPostsUser] = useState<{ id: string; name: string } | null>(null);
  const [fetchUserPosts, userPostsResult] = adminApi.useLazyGetAnalyticsUserPostsQuery();

  // Overview posts drill-in (single day or last-7-days window).
  const [postsRangeTitle, setPostsRangeTitle] = useState<string | null>(null);
  const [fetchPostsInRange, postsInRangeResult] = adminApi.useLazyGetAnalyticsPostsInRangeQuery();

  // Audience bucket drill-in (who's active / who signed up) + user modal.
  const [bucket, setBucket] = useState<BucketSelection | null>(null);
  const [modalUser, setModalUser] = useState<User | null>(null);

  const openBucket = (axis: PulseAxis, window: string, label: string) => {
    setBucket({ axis, window, label });
  };

  const openUserById = (u: { _id: string; displayName?: string; username?: string; email?: string }) => {
    // UserModal refetches the full user by _id; a minimal stub is enough to open it.
    setModalUser({
      _id: u._id,
      displayName: u.displayName || '',
      username: u.username || '',
      email: u.email || '',
    } as User);
  };

  const openUserFromMember = (m: AudienceMember) => openUserById(m);

  const queryParams = useMemo(() => {
    const params: Record<string, string | undefined> = {
      from: new Date(from + 'T00:00:00.000Z').toISOString(),
      to: new Date(to + 'T23:59:59.999Z').toISOString(),
      cohortMode: includeExcluded ? 'all' : 'traction',
      acquisitionSource: acquisitionSource || undefined,
      includeExcludedCohorts: includeExcluded ? 'true' : undefined,
    };
    return params;
  }, [from, to, acquisitionSource, includeExcluded]);

  const growth = adminApi.useGetAnalyticsGrowthQuery(queryParams, { skip: tab !== TAB.GROWTH });
  const overview = adminApi.useGetAnalyticsOverviewQuery(queryParams, { skip: tab !== TAB.OVERVIEW });
  const funnel = adminApi.useGetAnalyticsFunnelQuery(queryParams, { skip: tab !== TAB.FUNNEL });
  const content = adminApi.useGetAnalyticsContentQuery(queryParams, { skip: tab !== TAB.CONTENT });
  const social = adminApi.useGetAnalyticsSocialQuery(queryParams, { skip: tab !== TAB.SOCIAL });
  const retention = adminApi.useGetAnalyticsRetentionQuery(queryParams, { skip: tab !== TAB.RETENTION });
  const geography = adminApi.useGetAnalyticsGeographyQuery(queryParams, { skip: tab !== TAB.GEOGRAPHY });
  const cohorts = adminApi.useGetAnalyticsCohortsQuery(undefined, { skip: tab !== TAB.COHORTS });
  const engagement = adminApi.useGetAnalyticsEngagementQuery(queryParams, { skip: tab !== TAB.ENGAGEMENT });

  const openUserPosts = (id: string, name: string) => {
    setPostsUser({ id, name });
    fetchUserPosts(id);
  };

  // Open the posts modal for a single day (bar click).
  const openPostsForDay = (day: string) => {
    setPostsRangeTitle(`Posts on ${fmtDate(day)}`);
    fetchPostsInRange({
      ...queryParams,
      from: new Date(day + 'T00:00:00.000Z').toISOString(),
      to: new Date(day + 'T23:59:59.999Z').toISOString(),
    });
  };

  // Open the posts modal for the last-7-days window (metric card click).
  const openPostsLast7Days = () => {
    const now = new Date();
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    setPostsRangeTitle('Posts (last 7 days)');
    fetchPostsInRange({
      ...queryParams,
      from: weekAgo.toISOString(),
      to: now.toISOString(),
    });
  };

  const loading =
    (tab === TAB.GROWTH && growth.isLoading) ||
    (tab === TAB.OVERVIEW && overview.isLoading) ||
    (tab === TAB.FUNNEL && funnel.isLoading) ||
    (tab === TAB.CONTENT && content.isLoading) ||
    (tab === TAB.SOCIAL && social.isLoading) ||
    (tab === TAB.RETENTION && retention.isLoading) ||
    (tab === TAB.GEOGRAPHY && geography.isLoading) ||
    (tab === TAB.COHORTS && cohorts.isLoading) ||
    (tab === TAB.ENGAGEMENT && engagement.isLoading);

  return (
    <ThemeProvider theme={gridTheme}>
      <Box
        sx={{
          p: 3,
          minHeight: '100vh',
          bgcolor: gridTokens.bgPrimary,
          color: gridTokens.textPrimary,
        }}
      >
      <Typography variant="h4" gutterBottom>
        Analytics
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        By default this hides your own team, test, and mock accounts so you see real users only.
        Turn on the switch below to include everyone.
      </Typography>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="From"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="To"
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth>
            <InputLabel>Acquisition</InputLabel>
            <Select
              label="Acquisition"
              value={acquisitionSource}
              onChange={(e) => setAcquisitionSource(e.target.value)}
              disabled
            >
              <MenuItem value="">All sources</MenuItem>
              {ACQUISITION_SOURCES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={includeExcluded}
                onChange={(e) => setIncludeExcluded(e.target.checked)}
              />
            }
            label="Include team / test / mock accounts"
          />
        </Grid>
      </Grid>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" sx={{ mb: 2 }}>
        <Tab label="Growth" />
        <Tab label="Overview" />
        <Tab label="Funnel" />
        <Tab label="Content" />
        <Tab label="Social" />
        <Tab label="Retention" />
        <Tab label="Geography" />
        <Tab label="Cohorts" />
        <Tab label="Engagement" />
        <Tab label="SMS & Sign-ins" />
      </Tabs>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : null}

      {/* ---------------- GROWTH (plain English, default) ---------------- */}
      {!loading && tab === TAB.GROWTH && growth.data ? (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Click any number to see exactly who — and what they've been doing.
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <ActivityPulse
                title="Who's active"
                axis="active"
                dotColor="#2e7d32"
                onStatClick={openBucket}
                stats={[
                  { window: 'hour', label: 'Last hour', value: growth.data.counts?.activeLastHour },
                  { window: 'today', label: 'Today', value: growth.data.counts?.activeToday },
                  { window: 'day', label: 'Last 24h', value: growth.data.counts?.activeLast24h },
                  { window: 'week', label: 'Last 7 days', value: growth.data.counts?.active7d },
                ]}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ActivityPulse
                title="New signups"
                axis="signup"
                dotColor="#1976d2"
                onStatClick={openBucket}
                stats={[
                  { window: 'today', label: 'Today', value: growth.data.counts?.signupsToday },
                  { window: 'week', label: 'This week', value: growth.data.counts?.signups7d },
                  { window: 'month', label: 'Last 30 days', value: growth.data.counts?.signups30d },
                ]}
              />
              <Box sx={{ mt: 1, px: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  {growth.data.counts?.totalRealUsers?.toLocaleString?.() ??
                    growth.data.counts?.totalRealUsers}{' '}
                  total real users (excludes team / test / mock).
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
            Recent signups
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              {growth.data.rangeSummary?.signupsInRange ?? 0} in this date range •{' '}
              {growth.data.rangeSummary?.postedInRange ?? 0} have posted •{' '}
              {growth.data.rangeSummary?.completedProfileInRange ?? 0} completed their profile
            </Typography>
          </Typography>

          <Card variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Signed up</TableCell>
                  <TableCell>Where</TableCell>
                  <TableCell>Sign-in</TableCell>
                  <TableCell align="center">Profile</TableCell>
                  <TableCell align="center">Push</TableCell>
                  <TableCell align="right">Posts</TableCell>
                  <TableCell>Last active</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {(growth.data.signups || []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        No signups in this date range. Try widening the dates, or turn on
                        “Include team / test / mock accounts”.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  (growth.data.signups || []).map((u: any) => (
                    <TableRow key={u._id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Tooltip title="View user details">
                            <Avatar
                              src={audienceAvatarUrl(u)}
                              alt={u.displayName}
                              onClick={() => openUserById(u)}
                              sx={{
                                width: 32,
                                height: 32,
                                fontSize: 14,
                                cursor: 'pointer',
                                transition: 'box-shadow 0.15s',
                                '&:hover': { boxShadow: `0 0 0 2px ${gridTokens.accent}` },
                              }}
                            >
                              {(u.displayName || u.username || '?').charAt(0).toUpperCase()}
                            </Avatar>
                          </Tooltip>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={600} noWrap>
                              {u.displayName}
                              {u.isMockUser ? (
                                <Chip label="mock" size="small" sx={{ ml: 1 }} />
                              ) : null}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>
                              @{u.username}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>{fmtDate(u.createdAt)}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{audienceLocationText(u)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={u.authProvider}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        {u.profileCompleted ? (
                          <Chip label="Complete" size="small" color="success" variant="outlined" />
                        ) : (
                          <Chip label="Partial" size="small" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <PushChip pushable={!!u.pushable} />
                      </TableCell>
                      <TableCell align="right">{u.postCount}</TableCell>
                      <TableCell>{fmtRelative(u.lastActive)}</TableCell>
                      <TableCell align="right">
                        {u.postCount > 0 ? (
                          <Chip
                            label="View posts"
                            size="small"
                            color="primary"
                            variant="outlined"
                            onClick={() => openUserPosts(u._id, u.displayName)}
                            sx={{ cursor: 'pointer' }}
                          />
                        ) : (
                          <Typography variant="caption" color="text.disabled">
                            No posts
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </>
      ) : null}

      {/* ---------------- OVERVIEW ---------------- */}
      {!loading && tab === TAB.OVERVIEW && overview.data ? (
        <Grid container spacing={2}>
          <Grid item xs={6} md={3}>
            <MetricCard label="Total users" value={overview.data.totalUsers} />
          </Grid>
          <Grid item xs={6} md={3}>
            <MetricCard
              label="Completed profiles"
              value={overview.data.completedProfiles}
              help="Users who finished onboarding (name, photo, etc.)."
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <MetricCard
              label="DAU"
              value={overview.data.dailyActiveUsers}
              help="Daily Active Users: distinct people active in the last 24 hours (by last-active time)."
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <MetricCard
              label="WAU"
              value={overview.data.weeklyActiveUsers}
              help="Weekly Active Users: distinct people active in the last 7 days."
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <MetricCard
              label="Posts (last 7 days)"
              value={overview.data.nonFounderPostsThisWeek}
              onClick={openPostsLast7Days}
              help={
                includeExcluded
                  ? 'All posts created in the last 7 days (not soft-deleted), including team/test/mock authors. Click to view.'
                  : 'Posts created in the last 7 days by real users (excludes admin, test, and mock authors; not soft-deleted). Click to view.'
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Posts per day
                </Typography>
                <SimpleBarChart
                  data={overview.data.postsByDay || []}
                  color="#2e7d32"
                  onBarClick={(pt) => openPostsForDay(pt.date)}
                />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  New users per day
                </Typography>
                <SimpleBarChart data={overview.data.newUsersByDay || []} color="#1976d2" />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      ) : null}

      {/* ---------------- FUNNEL ---------------- */}
      {!loading && tab === TAB.FUNNEL && funnel.data ? (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Of the people who signed up in this date range, how many reached each step. “—” means
            we don’t track that step yet.
          </Typography>
          <Grid container spacing={2}>
            {[
              ['Signed up', funnel.data.signupCompleted, 'Accounts created in the date range.'],
              ['Completed profile', funnel.data.profileCompleted, 'Finished onboarding.'],
              [
                'Viewed content',
                funnel.data.viewedNeighborhoodPlaceOrUserGrid,
                'Requires screen-view tracking, which is not collected yet.',
              ],
              [
                'Posted / interacted',
                funnel.data.createdPostOrInteraction,
                'Created at least one post.',
              ],
              [
                'Returned within 1 day',
                funnel.data.returnedWithin1Day,
                'Was active at least a day after signing up (approximate).',
              ],
              [
                'Returned within 7 days',
                funnel.data.returnedWithin7Days,
                'Was active at least a week after signing up (approximate).',
              ],
              [
                'Activated',
                funnel.data.activatedUsers,
                'Reached the key first action (posted).',
              ],
            ].map(([label, value, help]) => (
              <Grid item xs={6} md={3} key={label as string}>
                <MetricCard label={label as string} value={value as number} help={help as string} />
              </Grid>
            ))}
          </Grid>
        </>
      ) : null}

      {/* ---------------- CONTENT ---------------- */}
      {!loading && tab === TAB.CONTENT && content.data ? (
        <Grid container spacing={2}>
          <Grid item xs={6} md={3}>
            <MetricCard label="Neighborhood posts" value={content.data.neighborhoodPosts} />
          </Grid>
          <Grid item xs={6} md={3}>
            <MetricCard label="Place posts" value={content.data.placePosts} />
          </Grid>
          <Grid item xs={6} md={3}>
            <MetricCard label="City posts" value={content.data.cityPosts} />
          </Grid>
          <Grid item xs={6} md={3}>
            <MetricCard label="Total posts (range)" value={content.data.totalPosts} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" sx={{ mt: 1 }}>
              Posts by neighborhood
            </Typography>
            <Table size="small">
              <TableBody>
                {(content.data.postsByNeighborhood || []).map((r: any) => (
                  <TableRow key={r.neighborhoodId}>
                    <TableCell>{r.name}</TableCell>
                    <TableCell align="right">{r.count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" sx={{ mt: 1 }}>
              Posts by venue
            </Typography>
            <Table size="small">
              <TableBody>
                {(content.data.postsByVenue || []).map((r: any) => (
                  <TableRow key={r.placeId}>
                    <TableCell>{r.name}</TableCell>
                    <TableCell align="right">{r.count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Grid>
        </Grid>
      ) : null}

      {/* ---------------- SOCIAL ---------------- */}
      {!loading && tab === TAB.SOCIAL && social.data ? (
        <Grid container spacing={2}>
          <Grid item xs={6} md={3}>
            <MetricCard
              label="Messages sent"
              value={social.data.messagesSent}
              help="Direct messages sent in the date range."
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <MetricCard
              label="Chats started"
              value={social.data.chatsStarted}
              help="New conversations started in the date range."
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <MetricCard
              label="People chatting"
              value={social.data.uniqueUsersInConversations}
              help="Distinct users who sent a message in the date range."
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <MetricCard
              label="Profiles viewed"
              value={social.data.profilesViewed}
              help="Not tracked yet (needs profile-view events)."
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <MetricCard
              label="Friends accepted"
              value={social.data.friendsAccepted}
              help="Not tracked with timestamps yet."
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <MetricCard
              label="Waves reciprocated"
              value={social.data.wavesReciprocated}
              help="Not tracked yet."
            />
          </Grid>
        </Grid>
      ) : null}

      {/* ---------------- RETENTION ---------------- */}
      {!loading && tab === TAB.RETENTION && retention.data ? (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Approximate: we compare signup time to the most recent activity time (we don’t store
            full session history yet).
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}>
              <MetricCard
                label="Day-1 retention"
                value={pct(retention.data.day1RetentionRate)}
                help="Of people who signed up in range, the share active at least 1 day later."
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <MetricCard
                label="Day-7 retention"
                value={pct(retention.data.day7RetentionRate)}
                help="Of people who signed up in range, the share active at least 7 days later."
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <MetricCard label="Signups in range" value={retention.data.signupCount} />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6">Weekly cohorts</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Week start</TableCell>
                    <TableCell align="right">Signups</TableCell>
                    <TableCell align="right">Back after 1d</TableCell>
                    <TableCell align="right">Back after 7d</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(retention.data.weeklyCohorts || []).map((r: any) => (
                    <TableRow key={r.weekStart}>
                      <TableCell>{r.weekStart}</TableCell>
                      <TableCell align="right">{r.signups}</TableCell>
                      <TableCell align="right">{r.day1}</TableCell>
                      <TableCell align="right">{r.day7}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Grid>
          </Grid>
        </>
      ) : null}

      {/* ---------------- GEOGRAPHY ---------------- */}
      {!loading && tab === TAB.GEOGRAPHY && geography.data ? (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6">Activity by neighborhood</Typography>
            <Table size="small">
              <TableBody>
                {(geography.data.activityByNeighborhood || []).map((r: any) => (
                  <TableRow key={r.neighborhoodId}>
                    <TableCell>{r.name}</TableCell>
                    <TableCell align="right">{r.events} posts</TableCell>
                    <TableCell align="right">{r.users} users</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="h6">Activity by venue</Typography>
            <Table size="small">
              <TableBody>
                {(geography.data.activityByVenue || []).map((r: any) => (
                  <TableRow key={r.placeId}>
                    <TableCell>{r.name}</TableCell>
                    <TableCell align="right">{r.events} posts</TableCell>
                    <TableCell align="right">{r.users} users</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Grid>
        </Grid>
      ) : null}

      {/* ---------------- COHORTS ---------------- */}
      {!loading && tab === TAB.COHORTS && cohorts.data ? (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6">Users by type</Typography>
            <Table size="small">
              <TableBody>
                {Object.entries(cohorts.data.usersByCohort || {}).map(([k, v]) => (
                  <TableRow key={k}>
                    <TableCell>{k.replace(/_/g, ' ')}</TableCell>
                    <TableCell align="right">{v as number}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="h6">Users by sign-in method</Typography>
            <Table size="small">
              <TableBody>
                {Object.entries(cohorts.data.usersByAuthProvider || {}).map(([k, v]) => (
                  <TableRow key={k}>
                    <TableCell>{k}</TableCell>
                    <TableCell align="right">{v as number}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Grid>
        </Grid>
      ) : null}

      {/* ---------------- ENGAGEMENT ---------------- */}
      {!loading && tab === TAB.ENGAGEMENT && engagement.data ? (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Product-engagement events captured from the app (taps, opens, feed
              impressions/dwell). Counts start at deploy; raw events expire after 90
              days. {includeExcluded ? 'Includes' : 'Excludes'} team/test/mock actors.
            </Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <MetricCard
              label="Total events"
              value={engagement.data.totalEvents}
              help="All product-analytics events recorded in the selected date range."
            />
          </Grid>
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ mt: 1 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Events per day
                </Typography>
                <SimpleBarChart data={engagement.data.eventsByDay || []} color="#7b1fa2" />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mt: 1, mb: 1 }}>
              By event
            </Typography>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell>
                    <strong>Event</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>Count</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>Unique users</strong>
                  </TableCell>
                </TableRow>
                {(engagement.data.byEvent || []).map(
                  (row: { name: string; count: number; uniqueUsers: number }) => (
                    <TableRow key={row.name}>
                      <TableCell>{row.name.replace(/_/g, ' ')}</TableCell>
                      <TableCell align="right">{row.count}</TableCell>
                      <TableCell align="right">{row.uniqueUsers}</TableCell>
                    </TableRow>
                  )
                )}
                {(engagement.data.byEvent || []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3}>
                      <Typography variant="body2" color="text.secondary">
                        No events recorded in this range yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </Grid>
        </Grid>
      ) : null}

      {/* ---------------- SMS & Sign-ins ---------------- */}
      {tab === TAB.SMS ? (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Every phone sign-in sends an SMS code, which costs money. Track spend and
            spot anyone hammering the code endpoint (possible abuse — candidates to ban).
          </Typography>
          <SmsSignInPanel onOpenUser={openUserById} />
        </>
      ) : null}

      {/* ---------------- User posts drill-in ---------------- */}
      <Dialog open={!!postsUser} onClose={() => setPostsUser(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Posts by {postsUser?.name}
          <IconButton onClick={() => setPostsUser(null)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {userPostsResult.isFetching ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (userPostsResult.data?.posts || []).length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              No posts found.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {(userPostsResult.data?.posts || []).map((p: AdminPost) => (
                <PostCard key={p._id} post={p} />
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* ---------------- Posts by day / range drill-in ---------------- */}
      <Dialog
        open={!!postsRangeTitle}
        onClose={() => setPostsRangeTitle(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {postsRangeTitle}
          {!postsInRangeResult.isFetching && postsInRangeResult.data ? (
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              {postsInRangeResult.data.total ?? (postsInRangeResult.data.posts || []).length} posts
            </Typography>
          ) : null}
          <IconButton onClick={() => setPostsRangeTitle(null)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {postsInRangeResult.isFetching ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (postsInRangeResult.data?.posts || []).length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              No posts found.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {(postsInRangeResult.data?.posts || []).map((p: AdminPost) => (
                <PostCard key={p._id} post={p} />
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* ---------------- Audience bucket drill-in ---------------- */}
      <BucketMembersDialog
        selection={bucket}
        includeExcluded={includeExcluded}
        onClose={() => setBucket(null)}
        onOpenUser={openUserFromMember}
      />

      {/* Full user detail (reuses the console's rich user modal). */}
      <UserModal
        isOpen={!!modalUser}
        user={modalUser}
        onClose={() => setModalUser(null)}
      />
      </Box>
    </ThemeProvider>
  );
};

export default AnalyticsPage;
